import { useInfiniteQuery } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { RouteLog } from "../../components/common/ObjectTypes";
import { parseCursor } from "../../components/activityLogs/ActivityLogsHelperFunctions";
import { queryKeys } from "../queryKeys";
import { getRouteLog } from "./routesApi";

// The filter fields /v2/logs/ understands, already flattened to query values.
export type RouteLogsQuery = {
    ordering: string,
    log_level: string,
    log_type: string,
    from_date: string,
    to_date: string,
    search: string,
};

type RouteLogsPage = { results: RouteLog[], next: string | null };

// Cancellation (a newer query replaced this one) and the per-request timeout
// are expected outcomes, not failures worth retrying.
const isCancelOrTimeout = (err: any) =>
    err?.name === "AbortError" || err?.name === "CanceledError"
    || err?.code === "ERR_CANCELED" || err?.code === "ECONNABORTED";

const REQUEST_TIMEOUT_MS = 30000;

// A Route's activity logs, newest page first, with infinite scroll.
// The query key is the Route, its integrations and the filter, so changing any
// of them starts a fresh first-page query and cancels the one in flight.
export const useRouteLogs = ({ routeId, integrationIds, query, enabled }: {
    routeId: string,
    integrationIds: string[],
    query: RouteLogsQuery,
    enabled: boolean,
}) => {
    const { keycloak } = useKeycloak();
    const token = keycloak.token;
    const result = useInfiniteQuery({
        queryKey: [...queryKeys.routes.logs(routeId), integrationIds, query],
        queryFn: async ({ pageParam, signal }): Promise<RouteLogsPage> => {
            const page = await getRouteLog(token as string, {
                ...query,
                cursor: pageParam,
                integration__in: integrationIds.toString(),
            }, signal, REQUEST_TIMEOUT_MS);
            return { results: page?.results ?? [], next: page?.next ?? null };
        },
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => parseCursor(lastPage.next ?? "") ?? undefined,
        enabled: enabled && !!token && !keycloak.isTokenExpired() && integrationIds.length > 0,
        retry: (failureCount, err) => !isCancelOrTimeout(err) && failureCount < 2,
        retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 10000),
        // A filter the user leaves is dropped at once, so coming back to it is a
        // fresh first page rather than a replay of every page scrolled before.
        gcTime: 0,
    });
    const pages = result.data?.pages ?? [];
    return {
        logs: pages.flatMap(page => page.results),
        nextCursor: pages.length ? parseCursor(pages[pages.length - 1].next ?? "") : null,
        loading: result.isFetching,
        appending: result.isFetchingNextPage,
        error: result.isError,
        fetchNextPage: result.fetchNextPage,
    };
};
